import { DeviceOSType } from '@/api/user-portal-api-generated';
import {
  getMobileAppAndroidPackage,
  getMobileAppDeepLinkScheme,
  getMobileAppIosAppStoreId,
} from '@/config/env-config';

const DEEP_LINK_SCHEME_PLACEHOLDER = '__MOBILE_APP_DEEP_LINK_SCHEME__';
const DEEP_LINK_SCHEME_FALLBACK = 'newcoreauth-dev';
const ANDROID_PACKAGE_PLACEHOLDER = '__MOBILE_APP_ANDROID_PACKAGE__';
const ANDROID_PACKAGE_FALLBACK = 'com.newcore.auth.dev';
const IOS_APP_STORE_ID_PLACEHOLDER = '__MOBILE_APP_IOS_APP_STORE_ID__';
const IOS_APP_STORE_ID_FALLBACK = '6754811883';
const ONBOARDING_DEEP_LINK_PATH = 'onboarding-entry';
const PAIRING_DEEP_LINK_PATH = 'token-entry';

function isValidEnvValue(value: string, placeholder: string) {
  return value.length > 0 && value !== placeholder;
}

export function getDeepLinkScheme() {
  const runtimeScheme = getMobileAppDeepLinkScheme();

  if (isValidEnvValue(runtimeScheme, DEEP_LINK_SCHEME_PLACEHOLDER)) {
    return runtimeScheme;
  }

  return DEEP_LINK_SCHEME_FALLBACK;
}

export function getAndroidPackage() {
  const runtimePackage = getMobileAppAndroidPackage();

  if (isValidEnvValue(runtimePackage, ANDROID_PACKAGE_PLACEHOLDER)) {
    return runtimePackage;
  }

  return ANDROID_PACKAGE_FALLBACK;
}

export function getIosAppStoreId() {
  const runtimeId = getMobileAppIosAppStoreId();

  if (isValidEnvValue(runtimeId, IOS_APP_STORE_ID_PLACEHOLDER)) {
    return runtimeId;
  }

  return IOS_APP_STORE_ID_FALLBACK;
}

/**
 * Returns the iOS App Store ID only when the runtime env var was actually
 * injected (no dev fallback). Use this for surfaces where falling back to a
 * dev/stub ID could silently send production users to the wrong app — most
 * notably the Apple Smart App Banner meta tag, which iOS renders natively
 * with no user-visible affordance to detect a mismatched app-id.
 */
export function getInjectedIosAppStoreId(): string | null {
  const runtimeId = getMobileAppIosAppStoreId();

  return isValidEnvValue(runtimeId, IOS_APP_STORE_ID_PLACEHOLDER)
    ? runtimeId
    : null;
}

export function getMobilePlatform() {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const userAgent = navigator.userAgent ?? '';

  if (/android/i.test(userAgent)) {
    return DeviceOSType.ANDROID;
  }

  if (/iphone|ipod/i.test(userAgent)) {
    return DeviceOSType.IOS;
  }

  return null;
}

export function createOnboardingDeepLinkUrl(token: string) {
  return `${getDeepLinkScheme()}://${ONBOARDING_DEEP_LINK_PATH}?token=${encodeURIComponent(token)}`;
}

export function createPairingDeepLinkUrl(pairingCode?: string) {
  const query = pairingCode
    ? `?pairingCode=${encodeURIComponent(pairingCode)}`
    : '';

  return `${getDeepLinkScheme()}://${PAIRING_DEEP_LINK_PATH}${query}`;
}

/**
 * Fire-and-forget deep link navigation. We don't observe whether the OS
 * actually handed control to the installed app — the install UI stays visible
 * regardless, so a visibility/timeout heuristic would be unused noise.
 */
export function attemptAppLaunch(deepLinkUrl: string) {
  try {
    window.location.href = deepLinkUrl;
  } catch {
    // Browser refused the navigation (e.g. blocked scheme); install UI is already shown.
  }
}
